import update from 'immutability-helper';
import _ from 'lodash';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { Section } from 'src/components/widgets/section';

import { T, backofficeUrl, getUrlWithQueryParams, openFileDialog, stringFormat, toGlobalID, toLocalID, uploadFile, urlToFile, uuid } from 'src/shared/helpers';

import { dialogsPortal } from 'src/shared/portals';
import { Mutable } from 'src/shared/types';
import styled from 'styled-components';
import { EcommerceId } from '../../../../../shared/enums';
import PageErrorIcon from 'src/shared/material_icons/legacy/page-error-icon.svg';
import theme from 'src/shared/theme';
import { Button, Link } from '../../../../widgets/buttons';
import { Icon } from '../../../../widgets/icon';
import Sparkles from 'src/components/widgets/svg/sparkles';
import { WizardFooterBackButton } from '../wizardFooterBackButton';
import { Dialog, DialogButton, DialogWindow, MessageDialog, useDialogManager } from '../../../../widgets/dialog';
import { AdvancedSelect } from '../../../../widgets/select';
import { ZTooltip } from '../../../../widgets/ztooltip';
import { PageFooterButtons } from '../../../../widgets/headers';
import { ContainerBase } from '../../../../widgets/layouts';
import { AIGeneratingIndicator, AiOutlineButton } from '../../../../widgets/ai';
import { ImageLightbox } from 'src/components/pages/aiStudio/components/layout/imageLightbox';
import { showNotification } from '../../../../widgets/notificationToaster';
import { Checkbox } from '../../../../widgets/checkbox';
import { Radio } from '../../../../widgets/radio';
import {
    addEmptyModelSideAction,
    addNewColorAction,
    editColorJsonSettingsAction,
    editColorMappingAction,
    editColorNameAction,
    editColorNameAndAttributesAction,
    editModelColorMappingsSalesChannelsAction,
    editSideMandatoryAllSides,
    editSideMandatoryAllVariants,
    editSideRotateAllSides,
    editSideRotateAllVariants,
    findModelColorForModelSide,
    getProductIsResizable,
    mergeModelSideAction,
    removeColorAction,
    removeModelOptionsAction,
    removeModelSideAction,
    setDisabledSidesAction,
    upsertModelOptionsAction,
    useProductConfigurationChangeStep,
    useProductConfigurationState
} from '../../state';
import { ModelColor, isDefaultColor as isDefaultModelColor } from '../../state/model/modelColor';
import { MerchantProductOption, ModelColorMapping } from '../../state/model/modelColorMapping';
import { ModelOptions } from '../../state/model/modelOptions';
import { ModelSide, defaultModelSideImageUrl } from '../../state/model/modelSide';
import { useModelSideManager } from '../../state/model/modelSideManager';
import { subscriptionQuery } from '../../state/queries/__generated__/subscriptionQuery.graphql';
import { subscription } from '../../state/queries/subscription';
import SelectedOptionValue from '../../state/selectedOptionValue';
import { Side } from '../../state/side';
import { PrintAreaEditorDialog } from '../3-printAreas/printAreaEditorDialog';
import { LargeMessageDialogWindow } from '../3-printAreas/printAreas';
import { generateSideImage } from '../3-printAreas/sideGenerator';
import { ModelArea, createAreaFromBoundsPX, createDefaultCenteredArea } from '../../state/model/modelArea';
import { AIStudioNoCreditsError } from 'src/components/pages/aiStudio/tasks';
import { useSideImagesNoCreditsDialog } from 'src/components/pages/productConfiguration/dialogs/sideImagesNoCreditsDialog';
import { useGenerationPreflight } from 'src/components/pages/aiStudio/hooks/useGenerationPreflight';
import UploadProgressRow from '../../../../widgets/uploadProgressRow';
import ConnectModelDialog, { PartialSalesChannel } from '../6-done/connectModelDialog';
import { variantsMerchantProductOptionsQuery } from './__generated__/variantsMerchantProductOptionsQuery.graphql';
import { variantsQuery } from './__generated__/variantsQuery.graphql';
import { variantsSetModelColorMappingMutation } from './__generated__/variantsSetModelColorMappingMutation.graphql';
import AddVariant from './addVariant';
import EditVariantDialog from './editVariantDialog';
import { ColorAttribute, MerchantProductVariantImageMatch, buildCombinations, matchColorsAttributes, merchantProductOptionType } from './helpers';
import JSONSettingsDialog from './jsonSettingsDialog';
import EditVariantsOffline from './offlineProducts/editVariantsOffline';
import MenuSourceVariants from './offlineProducts/menuSourceVariants';
import VariantsList from './variantsList';

const CustomDialogWindow = styled(DialogWindow)`
    flex: 1;
    align-self: stretch;
    justify-self: stretch;
    justify-content: center;
    margin: 0 80px;
    width: auto;
`;

const MiddleContent = styled.div`
    overflow: auto;
    max-height: 200px;
`;

const VariantContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const NoVariantsMessage = styled.p`
    margin: 40px auto;
    max-width: 550px;
    text-align: center;
    line-height: 22px;
`;

const ErrorIcon = styled(Icon)`
    height: 80px;
    width: 80px;
    margin: 20px auto 0;
    display: block;
`;

const SideContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

// Styling shared by the variant AI dialogs (bulk + single-side generation).
const BulkAiContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 4px 2px;
`;

const BulkAiField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const BulkAiLabel = styled.label`
    font-size: 13px;
    color: #57534e;
`;

const BulkAiCaption = styled.p`
    margin: 0;
    font-size: 13px;
    color: #78909c;
`;

const SideAiFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 8px;

    > button {
        min-width: 140px;
        justify-content: center;
    }
`;

// Title inside the content painted with the AI purple→pink gradient (matches the extra-options AI dialog).
const AiDialogTitle = styled.h3`
    width: fit-content;
    padding-right: 2px;
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: 600;
    background: linear-gradient(90deg, #6d28d9, #9333ea, #db2777, #9333ea, #6d28d9);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const BulkAiTextarea = styled.textarea`
    min-height: 72px;
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #263238;
    background: #fff;
    outline: none;
    resize: vertical;

    &:focus {
        border-color: ${theme.ai.solid};
    }
`;

interface SideForDialog {
    side: Side;
    firstCallback: () => void;
    allCallback: () => void;
    variantCallback: (color: string) => void;
    dialogKey: string;
}

interface Variant {
    code: string;
    side: Side;
    type: 'PRINT_AREA' | 'IMAGE';
    firstCallback: () => void;
    allCallback: () => void;
    variantCallback: (color: string) => void;
}
enum Select {
    all = 'all',
    thisOne = 'this one',
    thisOneInAllVariants = 'this one in all variants'
}

const VariantDialog: FC<Variant> = ({ code, side, firstCallback, allCallback, variantCallback }) => {
    const [selected, setSelected] = useState<string>(Select.thisOne);

    const { closeDialog } = useDialogManager();
    const codes = JSON.parse(code) as Array<Color>;

    const checkList =
        codes.length > 1 &&
        codes.map((x) => (
            <Radio
                label={T._('Change on all the variants with the attribute', 'Admin') + ' ' + (x.Value.Label ?? x.Value.Id)}
                checked={selected === x.Value.Id}
                onSelect={() => {
                    setSelected(x.Value.Id);
                }}
                name={x.Label}
                value={x.Value.Id}
            />
        ));
    const buttons: Array<DialogButton> = [
        {
            label: T._('Cancel', 'Admin'),
            outline: true,
            onClick: () => closeDialog('apply-image-variant')
        },
        {
            label: T._(`Apply`, 'Admin'),
            outline: false,
            onClick: () => {
                if (selected === Select.all) {
                    allCallback();
                    closeDialog('apply-image-variant');
                } else if (selected === Select.thisOne) {
                    firstCallback();
                    closeDialog('apply-image-variant');
                } else if (selected != Select.thisOne && selected != Select.all) {
                    variantCallback(selected);
                    closeDialog('apply-image-variant');
                }
            }
        }
    ];
    const message = stringFormat(T._('Select where you wish to apply the changes below:', 'Admin'), side.name);
    return (
        <Dialog windowDecorator={LargeMessageDialogWindow} title={message} buttons={buttons}>
            <MiddleContent>
                <VariantContainer>
                    <Radio
                        label={T._('Change on the current variant only', 'Admin')}
                        checked={selected === Select.thisOne}
                        onSelect={() => {
                            setSelected(Select.thisOne);
                        }}
                        name={'Change only this one'}
                        value={Select.thisOne}
                    />
                    <Radio
                        label={T._('Change on all the variants', 'Admin')}
                        checked={selected === Select.all}
                        onSelect={() => {
                            setSelected(Select.all);
                        }}
                        name={'Change All'}
                        value={Select.all}
                    />
                    {checkList && <>{checkList}</>}
                </VariantContainer>
            </MiddleContent>
        </Dialog>
    );
};

const SideDialog: FC<SideForDialog> = ({ side, firstCallback, allCallback, variantCallback, dialogKey }) => {
    const [selected, setSelected] = useState<string>(Select.thisOne);
    const { closeDialog } = useDialogManager();

    const buttons: Array<DialogButton> = [
        {
            label: T._('Cancel', 'Admin'),
            outline: true,
            onClick: () => closeDialog(dialogKey)
        },
        {
            label: T._(`Apply`, 'Admin'),
            outline: false,
            onClick: () => {
                if (selected === Select.all) {
                    allCallback();
                    closeDialog(dialogKey);
                } else if (selected === Select.thisOne) {
                    firstCallback();
                    closeDialog(dialogKey);
                } else if (selected != Select.thisOne && selected != Select.all) {
                    variantCallback(selected);
                    closeDialog(dialogKey);
                }
            }
        }
    ];
    const message = stringFormat(T._('Select where you wish to apply the changes below:', 'Admin'));

    return (
        <Dialog windowDecorator={LargeMessageDialogWindow} title={message} buttons={buttons}>
            <MiddleContent>
                <SideContainer>
                    <Radio
                        label={T._('Change on the current side only', 'Admin')}
                        checked={selected === Select.thisOne}
                        onSelect={() => {
                            setSelected(Select.thisOne);
                        }}
                        name={'Change only this one'}
                        value={Select.thisOne}
                    />
                    <Radio
                        label={T._('Change on all sides', 'Admin')}
                        checked={selected === Select.all}
                        onSelect={() => {
                            setSelected(Select.all);
                        }}
                        name={'Change all sides'}
                        value={Select.all}
                    />
                    <Radio
                        label={T._('Change on all sides named ' + side.name, 'Admin')}
                        checked={selected === Select.thisOneInAllVariants}
                        onSelect={() => {
                            setSelected(Select.thisOneInAllVariants);
                        }}
                        name={'Change on all sides named'}
                        value={Select.thisOneInAllVariants}
                    />
                </SideContainer>
            </MiddleContent>
        </Dialog>
    );
};

type BulkAiSource = 'existing' | 'scratch';

export interface BulkAiSideOption {
    guid: string;
    name: string;
}

export interface BulkAiGenerateOptions {
    prompt: string;
    source: BulkAiSource;
    generateImage: boolean;
    generatePrintArea: boolean;
    attributeFilter: string; // '' = all variations, otherwise an attribute value (e.g. "Black")
    sideFilter: string; // '' = all sides, otherwise a side guid
}

interface AiSelectOption<TValue extends string = string> {
    value: TValue;
    label: string;
}

const BulkAiCheckRow = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
`;

// Preview of the AI-generated image, shown before choosing where to apply it.
const PreviewSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding-top: 4px;
`;

const PreviewImage = styled.button`
    width: 180px;
    height: 180px;
    padding: 0;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    cursor: zoom-in;
    transition: border-color 0.2s;

    &:hover {
        border-color: ${theme.ai.solid};
    }

    img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }
`;

// Horizontal, scrollable strip of reference-image thumbnails (replaces the reference dropdown).
const ReferenceThumbList = styled.div`
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding: 2px 2px 6px;
`;

const ReferenceThumbItem = styled.button`
    flex: 0 0 auto;
    width: 76px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
`;

const ReferenceThumbImage = styled.div<{ $selected: boolean }>`
    width: 72px;
    height: 72px;
    border-radius: 10px;
    border: 2px solid ${(props) => (props.$selected ? '#9333ea' : '#e2e8f0')};
    box-shadow: ${(props) => (props.$selected ? '0 0 0 3px #ede9fe' : 'none')};
    background: #f8fafc;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        border-color 0.15s,
        box-shadow 0.15s;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

const ReferenceThumbLabel = styled.span<{ $selected: boolean }>`
    max-width: 72px;
    font-size: 11px;
    line-height: 1.2;
    text-align: center;
    color: ${(props) => (props.$selected ? '#6b21a8' : '#57534e')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

// AI-themed header banner (same purple/sparkle language used by the other AI dialogs, e.g. the side image source dialog).
const AiDialogHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1px solid #ddd6fe;
    background: linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%);
    color: #6b21a8;
    font-size: 13px;
    line-height: 1.4;

    ${Icon} {
        width: 20px;
        height: 20px;
        color: #9333ea;
        flex-shrink: 0;
    }
`;

const AiGenerateActionButton = styled(Button)`
    background: #7c3aed !important;
    border-color: #7c3aed !important;
    color: #fff !important;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:hover:not(:disabled) {
        background: #6d28d9 !important;
        border-color: #6d28d9 !important;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
    }

    ${Icon} {
        width: 16px;
        height: 16px;
    }
`;

const BulkAiImageDialog: FC<{
    attributeValues: string[];
    sides: BulkAiSideOption[];
    onGenerate: (options: BulkAiGenerateOptions) => void;
    onClose: () => void;
}> = ({ attributeValues, sides, onGenerate, onClose }) => {
    const [prompt, setPrompt] = useState('');
    const [source, setSource] = useState<BulkAiSource>('existing');
    const [generateImage, setGenerateImage] = useState(true);
    const [generatePrintArea, setGeneratePrintArea] = useState(false);
    const [attributeFilter, setAttributeFilter] = useState('');
    const [sideFilter, setSideFilter] = useState('');

    // A prompt is always required to generate images (both from scratch and image-to-image); it is not needed
    // (nor shown) when only the print area is generated.
    const imageNeedsPrompt = generateImage && !prompt.trim();
    const canGenerate = (generateImage || generatePrintArea) && !imageNeedsPrompt;

    const disabledReason =
        !generateImage && !generatePrintArea
            ? T._('Select at least one option to generate (Image or Print area).', 'Admin')
            : imageNeedsPrompt
              ? T._('A prompt is required to generate images.', 'Admin')
              : '';
    const sideOptions: AiSelectOption[] = [{ value: '', label: T._('All sides', 'Admin') }].concat(
        sides.map((side) => ({
            value: side.guid,
            label: side.name
        }))
    );
    const attributeOptions: AiSelectOption[] = [{ value: '', label: T._('All variants', 'Admin') }].concat(
        attributeValues.map((value) => ({
            value,
            label: stringFormat(T._('Only variants with the attribute: {0}', 'Admin'), value)
        }))
    );
    const sourceOptions: AiSelectOption<BulkAiSource>[] = [
        { value: 'existing', label: T._('Based on existing images', 'Admin') },
        { value: 'scratch', label: T._('Generate from scratch', 'Admin') }
    ];

    const generateButton = (
        <AiGenerateActionButton
            type='button'
            disabled={!canGenerate}
            onClick={() => onGenerate({ prompt: prompt.trim(), source, generateImage, generatePrintArea, attributeFilter, sideFilter })}
        >
            <Sparkles />
            {T._('Generate with AI', 'Admin')}
        </AiGenerateActionButton>
    );

    return (
        <Dialog windowDecorator={LargeMessageDialogWindow} onClose={onClose}>
            <BulkAiContent>
                <div>
                    <AiDialogTitle>{T._('Generate variant images and print areas with AI', 'Admin')}</AiDialogTitle>
                    <BulkAiCaption>{T._('Use AI to create or update variant images and print areas in bulk.', 'Admin')}</BulkAiCaption>
                </div>
                <BulkAiField>
                    <BulkAiLabel>{T._('What to generate', 'Admin')}</BulkAiLabel>
                    <BulkAiCheckRow>
                        <Checkbox label={T._('Image', 'Admin')} checked={generateImage} onChange={setGenerateImage} />
                        <Checkbox label={T._('Print area', 'Admin')} checked={generatePrintArea} onChange={setGeneratePrintArea} />
                    </BulkAiCheckRow>
                </BulkAiField>

                {sides.length > 1 && (
                    <BulkAiField>
                        <BulkAiLabel>{T._('Side', 'Admin')}</BulkAiLabel>
                        <AdvancedSelect<AiSelectOption>
                            isSearchable={false}
                            menuPosition='fixed'
                            value={sideOptions.find((option) => option.value === sideFilter)}
                            options={sideOptions}
                            onChange={(option) => setSideFilter(option?.value ?? '')}
                        />
                    </BulkAiField>
                )}

                <BulkAiField>
                    <BulkAiLabel>{T._('Apply to', 'Admin')}</BulkAiLabel>
                    <AdvancedSelect<AiSelectOption>
                        isSearchable={false}
                        menuPosition='fixed'
                        value={attributeOptions.find((option) => option.value === attributeFilter)}
                        options={attributeOptions}
                        onChange={(option) => setAttributeFilter(option?.value ?? '')}
                    />
                </BulkAiField>

                {/* Source and prompt only apply to image generation: hidden when generating the print area only. */}
                {generateImage && (
                    <>
                        <BulkAiField>
                            <BulkAiLabel>{T._('Source', 'Admin')}</BulkAiLabel>
                            <AdvancedSelect<AiSelectOption<BulkAiSource>>
                                isSearchable={false}
                                menuPosition='fixed'
                                value={sourceOptions.find((option) => option.value === source)}
                                options={sourceOptions}
                                onChange={(option) => setSource(option?.value ?? 'existing')}
                            />
                        </BulkAiField>

                        <BulkAiField>
                            <BulkAiLabel>{T._('Prompt', 'Admin')}</BulkAiLabel>
                            <BulkAiTextarea
                                value={prompt}
                                placeholder={T._('Guide the AI, e.g. describe the product, style or angle to use for every variant.', 'Admin')}
                                onChange={(e) => setPrompt(e.currentTarget.value)}
                            />
                        </BulkAiField>
                    </>
                )}

                <SideAiFooter>
                    {!canGenerate ? (
                        <ZTooltip title={disabledReason}>
                            <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>{generateButton}</span>
                        </ZTooltip>
                    ) : (
                        generateButton
                    )}
                </SideAiFooter>
            </BulkAiContent>
        </Dialog>
    );
};

// A distinct product image (deduplicated across variants/sides), usable as a reference for AI generation.
export interface ExistingSideRef {
    imageUrl: string;
    label: string;
}

// Options chosen in the AI generation dialog.
export interface SideImageAiOptions {
    mode: 'scratch' | 'existing';
    referenceImageUrl: string | null;
    prompt: string;
}

// AI-only single-side generation dialog — same select + prompt style as the bulk dialog. Shows a preview
// of the generated image (with the option to regenerate) before choosing where to apply it.
// Shared with the product setup page (print areas step) so both open the exact same modal.
export const SideAiGenerateDialog: FC<{
    existingSides: ExistingSideRef[];
    // Reference image preselected when "based on existing images" is chosen (the side's current image when
    // editing, or the first side of the same variant for a new product). Falls back to the first reference.
    defaultReferenceImageUrl?: string | null;
    generatePreview: (opts: SideImageAiOptions) => Promise<string>;
    onApply: (url: string) => void;
    onClose: () => void;
    onNoCredits: () => void;
    // Whether to offer the "based on existing images" source. Shown always except, on the product
    // setup page, when there is a single side (no other side to reference). Defaults to "has references".
    allowExistingOption?: boolean;
}> = ({ existingSides, defaultReferenceImageUrl, generatePreview, onApply, onClose, onNoCredits, allowExistingOption }) => {
    const hasExisting = existingSides.length > 0;
    const showExistingOption = allowExistingOption ?? hasExisting;
    const [source, setSource] = useState<'scratch' | 'existing'>(showExistingOption && hasExisting ? 'existing' : 'scratch');
    const [referenceImageUrl, setReferenceImageUrl] = useState(defaultReferenceImageUrl ?? existingSides[0]?.imageUrl ?? '');
    const [prompt, setPrompt] = useState('');
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { runWithPreflight } = useGenerationPreflight();
    const [showZoom, setShowZoom] = useState(false);

    // Generating from scratch requires a prompt (there is no reference image to start from).
    // This dialog always generates an image, so a prompt is always required (both from scratch and image-to-image).
    const promptRequired = !prompt.trim();
    const sourceOptions: AiSelectOption<'scratch' | 'existing'>[] = [
        ...(showExistingOption ? [{ value: 'existing' as const, label: T._('Based on existing images', 'Admin') }] : []),
        { value: 'scratch', label: T._('Generate from scratch', 'Admin') }
    ];
    const handleGenerate = async () => {
        setError(null);
        // Gate the generation on AI Studio availability: activates the addon when it is not active
        // (granting the free first pack) and opens the purchase dialog on NO_CREDITS, before any task is queued.
        await runWithPreflight(
            async () => {
                setGenerating(true);
                try {
                    const url = await generatePreview({
                        mode: source === 'existing' ? 'existing' : 'scratch',
                        referenceImageUrl: source === 'existing' ? referenceImageUrl || existingSides[0]?.imageUrl || null : null,
                        prompt: prompt.trim()
                    });
                    setPreviewUrl(url);
                } catch (ex) {
                    if (ex instanceof AIStudioNoCreditsError) {
                        // Credits exhausted mid-run: hand off to the purchase dialog instead of an inline dead-end.
                        onNoCredits();
                    } else {
                        setError(T._('The side image could not be generated. Please try again.', 'Admin'));
                    }
                } finally {
                    setGenerating(false);
                }
                // Side generation must not enable credit auto-refill on first addon activation (unlike the other AI Studio tools).
            },
            { autoRefill: false, onNoCredits }
        );
    };

    return (
        <Dialog windowDecorator={LargeMessageDialogWindow} onClose={onClose}>
            <BulkAiContent>
                <div>
                    <AiDialogTitle>{T._('Generate product image with AI', 'Admin')}</AiDialogTitle>
                    <BulkAiCaption>
                        {showExistingOption
                            ? T._('Generate this side image with AI, from scratch or based on an existing image.', 'Admin')
                            : T._('Describe your product and the AI will generate the product image for you', 'Admin')}
                    </BulkAiCaption>
                </div>

                {showExistingOption && (
                    <BulkAiField>
                        <BulkAiLabel>{T._('Source', 'Admin')}</BulkAiLabel>
                        <AdvancedSelect<AiSelectOption<'scratch' | 'existing'>>
                            isSearchable={false}
                            menuPosition='fixed'
                            value={sourceOptions.find((option) => option.value === source)}
                            options={sourceOptions}
                            onChange={(option) => setSource(option?.value ?? 'scratch')}
                        />
                    </BulkAiField>
                )}

                {source === 'existing' && existingSides.length > 1 && (
                    <BulkAiField>
                        <BulkAiLabel>{T._('Reference image', 'Admin')}</BulkAiLabel>
                        <ReferenceThumbList>
                            {existingSides.map((side) => {
                                const selected = side.imageUrl === referenceImageUrl;
                                return (
                                    <ReferenceThumbItem
                                        key={side.imageUrl}
                                        type='button'
                                        aria-label={side.label}
                                        aria-pressed={selected}
                                        onClick={() => setReferenceImageUrl(side.imageUrl)}
                                    >
                                        <ReferenceThumbImage $selected={selected}>
                                            <img src={side.imageUrl} alt={side.label} />
                                        </ReferenceThumbImage>
                                        <ReferenceThumbLabel $selected={selected}>{side.label}</ReferenceThumbLabel>
                                    </ReferenceThumbItem>
                                );
                            })}
                        </ReferenceThumbList>
                    </BulkAiField>
                )}

                <BulkAiField>
                    <BulkAiLabel>{T._('Prompt', 'Admin')}</BulkAiLabel>
                    <BulkAiTextarea
                        value={prompt}
                        placeholder={
                            source === 'scratch'
                                ? T._('Describe the image to generate, e.g. the product, its style, colors or angle.', 'Admin')
                                : T._('Describe how to adjust the existing image, e.g. change the angle, background or colors.', 'Admin')
                        }
                        onChange={(e) => setPrompt(e.currentTarget.value)}
                    />
                </BulkAiField>

                {error && <div style={{ color: '#dc2626', fontSize: 12 }}>{error}</div>}

                {generating && (
                    <AIGeneratingIndicator
                        phrases={[
                            T._('Got it — generating your product image...', 'Admin'),
                            T._('Setting up the scene...', 'Admin'),
                            T._('Rendering the product...', 'Admin'),
                            T._('Almost ready...', 'Admin')
                        ]}
                    />
                )}

                {previewUrl && !generating && (
                    <PreviewSection>
                        <BulkAiLabel>{T._('Preview', 'Admin')}</BulkAiLabel>
                        <PreviewImage type='button' onClick={() => setShowZoom(true)} title={T._('Click to zoom', 'Admin')}>
                            <img src={previewUrl} alt={T._('AI generated product image', 'Admin')} />
                        </PreviewImage>
                    </PreviewSection>
                )}

                {showZoom && previewUrl && (
                    <ImageLightbox
                        imageUrl={previewUrl}
                        alt={T._('AI generated product image', 'Admin')}
                        showDownload={false}
                        onClose={() => setShowZoom(false)}
                    />
                )}

                {!previewUrl && !generating && (
                    <SideAiFooter>
                        <AiGenerateActionButton type='button' disabled={promptRequired} onClick={handleGenerate}>
                            <Sparkles />
                            {T._('Generate', 'Admin')}
                        </AiGenerateActionButton>
                    </SideAiFooter>
                )}

                {previewUrl && !generating && (
                    <SideAiFooter>
                        <AiOutlineButton type='button' disabled={promptRequired} onClick={handleGenerate}>
                            <Sparkles />
                            {T._('Regenerate', 'Admin')}
                        </AiOutlineButton>
                        <AiGenerateActionButton type='button' onClick={() => onApply(previewUrl)}>
                            {T._('Apply', 'Admin')}
                        </AiGenerateActionButton>
                    </SideAiFooter>
                )}
            </BulkAiContent>
        </Dialog>
    );
};

interface Value {
    Id: string;
    Label: string;
}
interface Color {
    Id: string;
    Label: string;
    Value: Value;
}
const VariantsPage: FC<{}> = () => {
    // Hooks
    const navigate = useNavigate();
    const location = useLocation();

    const changeStep = useProductConfigurationChangeStep();
    const { changeImage, setImageFromUrl } = useModelSideManager();
    const { state, dispatch } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const { showSideImagesNoCreditsDialog } = useSideImagesNoCreditsDialog();
    const { runWithPreflight } = useGenerationPreflight();
    const routeParams = useParams();

    // Queries
    const query = useLazyLoadQuery<variantsQuery>(
        graphql`
            query variantsQuery($sku: String!, $salesChannelID: ID!) {
                ecommerceSettings {
                    ecommerceName
                }

                salesChannels(status: ACTIVE, ignoreNoneECommerce: true) {
                    id
                    ecommerceSettings {
                        ecommerceID
                        ecommerceName
                        hasProductsApi
                        isMarketplace
                        remoteUrl
                        definitiveUrl
                    }
                }

                merchantProductOptions(sku: $sku, salesChannelID: $salesChannelID) {
                    optionID
                    label
                    isGlobal
                    values {
                        valueID
                        label
                    }
                }

                seller {
                    marketplace
                }
                generalSettings {
                    isSideImageScalingEnabled
                }
            }
        `,
        { sku: state.product.code || '', salesChannelID: state.salesChannelID }
    );

    const merchantProductOptionsConnected =
        state.product.id === null
            ? []
            : (useLazyLoadQuery<variantsMerchantProductOptionsQuery>(
                  graphql`
                      query variantsMerchantProductOptionsQuery($productID: ID!) {
                          model(id: $productID) {
                              merchantProductConnectedOptions {
                                  salesChannelID
                                  sku
                                  optionID
                                  label
                                  isGlobal
                                  values {
                                      valueID
                                      label
                                  }
                              }
                          }
                      }
                  `,
                  { productID: state.product.id! }
              ).model?.merchantProductConnectedOptions ?? []);

    // Mutations

    const setModelColorMappingMutation = graphql`
        mutation variantsSetModelColorMappingMutation($modelID: ID!, $modelColorMappings: [ModelColorMappingInput!]!) {
            setModelColorMapping(modelID: $modelID, modelColorMappings: $modelColorMappings) {
                colorID
                merchantCode
            }
        }
    `;

    const [setModelColorMappings, isMappingInProgress] = useMutation<variantsSetModelColorMappingMutation>(setModelColorMappingMutation);

    const [commitPredictPrintAreas] = useMutation(graphql`
        mutation variantsPredictPrintAreasMutation($input: PredictPrintAreasInput!) {
            predictPrintAreas(input: $input) {
                success
                error
                imageInfo {
                    width
                    height
                    ppcmAvg
                }
                detections {
                    confidence
                    bboxPixels {
                        x
                        y
                        width
                        height
                    }
                }
            }
        }
    `);

    // State
    const [currentEditingModelSide, setCurrentEditingModelSide] = useState<ModelSide | null>(null);
    const [currentEditingModelOptions, setCurrentEditingModelOptions] = useState<ModelOptions | null>(null);

    const [modelOptions, setModelOptions] = useState<merchantProductOptionType[]>(() => {
        let modelColorsCodes = state.product.colors.map((c) => JSON.parse(c.code || '[]') as ColorAttribute[]);
        let modelOptions: merchantProductOptionType[] = [];
        if (state.product.colors.length > 0) {
            for (let i = 0; i < modelColorsCodes[0].length; i++) {
                modelOptions.push({
                    isGlobal: false,
                    optionID: modelColorsCodes[0][i].Id,
                    label: modelColorsCodes[0][i].Label,
                    values: []
                });
                modelColorsCodes.forEach((m) => {
                    m[i] &&
                        modelOptions[i].values.push({
                            valueID: m[i].Value.Id,
                            label: m[i].Value.Label
                        });
                });
                modelOptions[i].values = [...new Set(modelOptions[i].values.map((o) => JSON.stringify(o)))].map((o2) => JSON.parse(o2));
            }
        }

        return modelOptions;
    });

    const areVariantsSupported = state.supportedVariants;

    const hasMultipleOptions =
        query &&
        query.merchantProductOptions &&
        query.merchantProductOptions.length > 0 &&
        (query.merchantProductOptions.length > 1 || query.merchantProductOptions[0].values.length > 1);

    const hasCompleteAttributes = (() => {
        if (state.product.colors.length < 1 || state.product.colors[0].code === '[]') return false;
        try {
            const codes = JSON.parse(state.product.colors[0].code) as SelectedOptionValue[];
            return codes.length > 0 && codes.every((c) => c.Label !== '' && c.Value.Label !== '');
        } catch {
            return false;
        }
    })();
    const hasVariants = hasCompleteAttributes || hasMultipleOptions;
    const subscriptionResponse = useLazyLoadQuery<subscriptionQuery>(subscription, {});
    const [showMappingDialog, setShowMappingDialog] = useState(false);
    const [fixedVariantGuids, setFixedVariantGuids] = useState<Set<string>>(new Set());

    //This state was introduced to handle the variant images based on the default image size, without taking scaling settings into account at this stage.
    const [isDefaultImageScaled, setIsDefaultImageScaled] = useState<boolean>(false);

    const selectedSalesChannel = query.salesChannels.find((s) => s.id === state.salesChannelID)?.ecommerceSettings?.ecommerceID;

    const showBannerEcommerceIDs = [
        EcommerceId.MAGENTO2,
        EcommerceId.SHOPIFY,
        EcommerceId.PRESTASHOP,
        EcommerceId.BIGCOMMERCE,
        EcommerceId.WOOCOMMERCE,
        EcommerceId.ETSY
    ];

    let unsupportedSalesChannelsVariantsIDs = useMemo(() => {
        return query.salesChannels
            .filter((sc) => {
                return sc.ecommerceSettings?.ecommerceName === 'AMAZON';
            })
            .map((sc) => sc.id);
    }, [query.salesChannels]);

    //const showBanner = modelOptions?.length === 0; lascio commentato, se è true serve per mostrare l'info banner solo se non sono ancora state importate varianti

    const importImageVariantForSalesChannel = showBannerEcommerceIDs.includes(Number(selectedSalesChannel));

    // Detect mapping mismatch: compare variant attributes saved in Zakeke colors with current store options.
    // Computed once on mount so that editing individual variants doesn't clear the mismatch for the rest.
    // Skipped for from-scratch products (no e-commerce store to compare against).
    const currentSalesChannel = query.salesChannels.find((s) => s.id === state.salesChannelID);
    const isFromScratch = currentSalesChannel?.ecommerceSettings?.ecommerceName === 'NONE';
    const mismatchedVariantGuids = useRef<Set<string> | null>(null);
    if (mismatchedVariantGuids.current === null) {
        const result = new Set<string>();
        if (!isFromScratch) {
            const colorsWithAttributes = state.product.colors.filter((c) => c.code && c.code !== '[]');

            if (colorsWithAttributes.length > 0 && query.merchantProductOptions.length > 0) {
                const storeLabelByOptionID = new Map<string, string>();
                const storeValuesByOption = new Map<string, Set<string>>();
                for (const opt of query.merchantProductOptions) {
                    storeLabelByOptionID.set(opt.optionID, opt.label);
                    storeValuesByOption.set(opt.optionID, new Set(opt.values.map((v) => v.valueID)));
                }

                for (const color of colorsWithAttributes) {
                    const attrs = JSON.parse(color.code) as ColorAttribute[];
                    for (const attr of attrs) {
                        const storeLabel = storeLabelByOptionID.get(attr.Id);
                        const storeValues = storeValuesByOption.get(attr.Id);

                        if (
                            (storeLabel && storeLabel.toLowerCase() !== attr.Label.toLowerCase()) ||
                            (storeValues && attr.Value.Id && !storeValues.has(attr.Value.Id)) ||
                            (!storeLabel && attr.Id)
                        ) {
                            result.add(color.guid);
                            break;
                        }
                    }
                }
            }
        }
        mismatchedVariantGuids.current = result;
    }
    const hasMappingMismatch = mismatchedVariantGuids.current.size > 0 && mismatchedVariantGuids.current.size > fixedVariantGuids.size;

    const colors = useMemo(
        () =>
            _.sortBy(
                state.product.colors.filter((x) => !isDefaultModelColor(x)),
                (color) => color.position
            ),
        [state.product.colors]
    );

    const storeVariantsRemoved =
        areVariantsSupported &&
        !hasMultipleOptions &&
        hasCompleteAttributes &&
        colors.length > 0 &&
        !state.ecommerceSalesChannel.some((e) => e === 'NONE') &&
        (!query?.merchantProductOptions || query.merchantProductOptions.length === 0);

    //If all variants have been removed, redirect to the Review page with a notification.
    useEffect(() => {
        if (
            routeParams.page === 'variations' &&
            routeParams.subpage === 'list' &&
            colors.length === 0 &&
            !state.ecommerceSalesChannel.some((e) => e === 'NONE') &&
            state.ecommerceSalesChannel.length === 1
        ) {
            showNotification({
                type: 'success',
                message: <>{T._('Variants removed successfully. Remember to save to apply the changes.', 'Admin')}</>,
                dismiss: {
                    duration: 3000
                }
            });
        }
    }, [routeParams, colors, state.ecommerceSalesChannel, navigate]);

    async function wasDefaultImageScaled(url: string) {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        const load = new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
        });

        img.src = url;
        await load;

        const result = img.naturalWidth <= 1024 && img.naturalHeight <= 1024;
        return result;
    }

    useEffect(() => {
        const url = state.product?.colors?.[0]?.sides?.[0]?.imageUrl;

        if (!url) return;

        let cancelled = false;

        async function checkImage() {
            try {
                const result = await wasDefaultImageScaled(url);
                if (!cancelled) {
                    setIsDefaultImageScaled(result);
                }
            } catch (e) {
                console.error('Error checking image:', e);
            }
        }

        checkImage();
        return () => {
            cancelled = true;
        };
    }, []);

    // Auto-open print area editor when variantId and sideId are in the URL (from product sidebar)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const variantId = params.get('variantId');
        const sideIdParam = params.get('sideId');
        if (!variantId || !sideIdParam) return;

        const sideId = parseInt(sideIdParam, 10);
        const modelColor = state.product.colors.find((c) => c.colorID === parseInt(variantId, 10));
        if (!modelColor || isNaN(sideId)) return;

        const modelSide = modelColor.sides.find((s) => s.sideID === sideId);
        if (!modelSide) return;

        setCurrentEditingModelSide(modelSide);
        setCurrentEditingModelOptions(
            state.product.options.find((x) => x.colorGuid === modelColor.guid && (x.sideGuid === modelSide?.guid || x.sideGuid === null)) ?? null
        );
        navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/list/print-area-editor')), { state: location.state });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Callbacks
    const handleAddVariationClick = async (
        selectedOptionsValues: SelectedOptionValue[],
        merchantProductVariantImageMatches: MerchantProductVariantImageMatch[] | null
    ) => {
        if (selectedOptionsValues.length == 0) {
            showDialog('attributes-error', <MessageDialog message={T._('Select at last one attribute.', 'Admin')} />);
        } else {
            // Build combinations
            const combinations = buildCombinations(selectedOptionsValues);

            if (combinations.length > 300) {
                showNotification({
                    type: 'info',
                    message: (
                        <>
                            {T._(
                                'You are adding a large number of variants. This operation could take some time to complete. Please do not close the page.',
                                'Admin'
                            )}
                        </>
                    ),
                    dismiss: {
                        duration: 10000
                    }
                });
            }
            const skippedVariants: string[] = [];

            for (const combination of combinations) {
                // Check if variant already exists
                const alreadyExists = state.product.colors.some((x) => matchColorsAttributes(x.code, combination));
                if (alreadyExists) {
                    const variantName = combination.map((c) => c.Value.Label || c.Value.Id).join(' / ');
                    skippedVariants.push(variantName);
                    continue;
                }
                let imageUrl = null;
                if (merchantProductVariantImageMatches) {
                    const match = merchantProductVariantImageMatches.find(
                        (m) =>
                            m.selections &&
                            m.selections.every((s) =>
                                combination.some(
                                    (c) => (c.Id === s.optionID || c.Label === s.label) && (c.Value.Id === s.value.id || c.Value.Label === s.value.id)
                                )
                            )
                    );
                    if (match) {
                        let url = match.imageUrl;
                        if (isDefaultImageScaled) {
                            //convert store image url to File obj, to scale it if needed
                            const file = await urlToFile(match.imageUrl, 'store-image');
                            url = await uploadFile(file, isDefaultImageScaled);
                        }
                        imageUrl = url;
                    }
                }

                dispatch(addNewColorAction(combination, imageUrl));
            }

            if (skippedVariants.length > 0) {
                const allSkipped = skippedVariants.length === combinations.length;
                showNotification({
                    type: 'warning',
                    message: (
                        <>
                            {allSkipped
                                ? T._(
                                      'All selected variants have already been imported. If they are not visible, the existing variants may be mapped incorrectly. Click the edit icon on each variant to review and fix the mapping.',
                                      'Admin'
                                  )
                                : stringFormat(
                                      T._(
                                          'The following variants were not imported because they are already configured: {0}. If they are not visible, the existing variants may be mapped incorrectly. Click the edit icon on each variant to review and fix the mapping.',
                                          'Admin'
                                      ),
                                      skippedVariants.join(', ')
                                  )}
                        </>
                    ),
                    dismiss: {
                        duration: 10000
                    }
                });
            }

            let supportedAdditionalCodesForVariants = state.product.additionalCodes.filter((ac) => {
                return !unsupportedSalesChannelsVariantsIDs.some((x) => toLocalID(x) === toLocalID(ac.salesChannelId));
            });

            // If all variants were skipped, navigate to variants list
            if (skippedVariants.length === combinations.length) {
                navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/list/')));
                return;
            }

            if (state.product.id !== null && supportedAdditionalCodesForVariants.length > 0) setShowMappingDialog(true);
            else navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/list/')));
        }
    };

    const handleVariantRemoveClick = (modelColor: ModelColor) => {
        dispatch(removeColorAction(modelColor));
        let mappings = state.product.colorMappings.filter((m) => m.colorID != modelColor.colorID || (m.productColor && m.productColor.guid != modelColor.guid));
        dispatch(editModelColorMappingsSalesChannelsAction(mappings));
    };

    const handleVariantEditClick = (modelColor: ModelColor) => {
        showDialog(
            'color-name',
            <EditVariantDialog
                name={modelColor.name}
                mainSalesChannelID={state.salesChannelID}
                mainSalesChannelRemoteUrl={query.salesChannels.find((sc) => sc.id === state.salesChannelID)?.ecommerceSettings?.remoteUrl ?? 'Offline'}
                modelColor={modelColor}
                modelColorCode={JSON.parse(modelColor.code || '[]') as ColorAttribute[]}
                modelOptions={
                    state.ecommerceSalesChannel.some((e) => e !== 'NONE') && state.ecommerceSalesChannel.length == 1
                        ? (query.merchantProductOptions as merchantProductOptionType[])
                        : modelOptions
                }
                additionalCodes={state.product.additionalCodes.filter((ac) => {
                    return !unsupportedSalesChannelsVariantsIDs.some((x) => toLocalID(x) === toLocalID(ac.salesChannelId));
                })}
                colorMappings={state.product.colorMappings.filter((cm) => cm.colorID == modelColor.colorID || cm.productColor?.guid == modelColor.guid)}
                mainStoreVariantsRemoved={storeVariantsRemoved}
                onConfirm={(name, attributes, colorMapping, onError) => {
                    onConfirmVariantEdit(modelColor, name, attributes, colorMapping, onError);
                }}
            />
        );
    };

    const onConfirmVariantEdit = (
        modelColor: ModelColor,
        name: string,
        attributes: ColorAttribute[] | null | undefined,
        colorMapping: ModelColorMapping | null | undefined,
        onError: () => void
    ) => {
        // A variation must have a unique attribute combination. Block confirming an edit that would make
        // this variant share the same attributes as another existing variant.
        if (attributes && attributes.length > 0) {
            const hasDuplicateAttributes = state.product.colors.some(
                (color) => color.guid !== modelColor.guid && color.code && matchColorsAttributes(color.code, attributes)
            );

            if (hasDuplicateAttributes) {
                showNotification({
                    type: 'danger',
                    message: <>{T._('Another variant is already using the selected attributes. Please choose a different combination.', 'Admin')}</>,
                    dismiss: {
                        duration: 3000
                    }
                });
                onError();
                return;
            }
        }

        if (colorMapping && state.product.id && attributes) {
            let newColorMappings = [...state.product.colorMappings];
            newColorMappings = newColorMappings.filter((mc) => mc.colorID != colorMapping.colorID || mc.productColor?.guid != colorMapping.productColor?.guid);
            newColorMappings.push(colorMapping);

            if (colorMapping.colorID > 0) {
                setModelColorMappings({
                    variables: {
                        modelID: state.product.id,
                        modelColorMappings: newColorMappings.map((mapp) => {
                            let { productColor, productSKU, ...newMapp } = mapp;
                            return newMapp;
                        })
                    },
                    onError: (error) => {
                        showNotification({
                            type: 'danger',
                            message: <>{T._('An error occurred while mapping product variants. Please try again.', 'Admin')}</>,
                            dismiss: {
                                duration: 3000
                            }
                        });
                        onError();
                    },
                    onCompleted: (response) => {
                        dispatch(editColorNameAndAttributesAction(modelColor, name, attributes));
                        dispatch(editColorMappingAction(colorMapping));
                        dispatch(editModelColorMappingsSalesChannelsAction(newColorMappings));
                        setFixedVariantGuids((prev) => new Set(prev).add(modelColor.guid));
                        closeDialog('color-name');
                    }
                });
            } else {
                dispatch(editColorNameAndAttributesAction(modelColor, name, attributes));
                dispatch(editColorMappingAction(colorMapping));
                dispatch(editModelColorMappingsSalesChannelsAction(newColorMappings));
                setFixedVariantGuids((prev) => new Set(prev).add(modelColor.guid));
                closeDialog('color-name');
            }
        } else if (attributes) {
            dispatch(editColorNameAndAttributesAction(modelColor, name, attributes));
            setFixedVariantGuids((prev) => new Set(prev).add(modelColor.guid));
            closeDialog('color-name');
        } else {
            dispatch(editColorNameAction(modelColor, name));
            setFixedVariantGuids((prev) => new Set(prev).add(modelColor.guid));
            closeDialog('color-name');
        }
    };

    const realImageUrl = (url?: string | null): string | undefined => (url && url !== defaultModelSideImageUrl ? url : undefined);

    // Every distinct product image (across all variants and sides), usable as a reference for image-to-image
    // AI generation. Deduplicated by image; the color name is added to the label only when there are multiple variants.
    const buildExistingImageRefs = (): ExistingSideRef[] => {
        const hasMultipleColors = state.product.colors.length > 1;
        const refs: ExistingSideRef[] = [];
        const seen = new Set<string>();
        state.product.colors.forEach((color) => {
            color.sides.forEach((ms) => {
                const img = realImageUrl(ms.imageUrl);
                if (!img || seen.has(img)) return;
                seen.add(img);
                const sideName = state.sides.find((s) => s.guid === ms.originalSideGuid)?.name ?? T._('Side', 'Admin');
                refs.push({ imageUrl: img, label: hasMultipleColors && color.name ? `${color.name} / ${sideName}` : sideName });
            });
        });
        return refs;
    };

    // Default reference image: the target side's current image when it has one (edit), otherwise the first
    // side's image of the same variant (new product). Undefined falls back to the first available reference.
    const pickDefaultReferenceImage = (targetSide?: ModelSide): string | undefined => {
        const current = realImageUrl(targetSide?.imageUrl);
        if (current) return current;
        const color = targetSide ? state.product.colors.find((c) => c.sides.some((s) => s.guid === targetSide.guid)) : state.product.colors[0];
        const firstSideGuid = state.sides[0]?.guid;
        return realImageUrl(color?.sides.find((s) => s.originalSideGuid === firstSideGuid)?.imageUrl);
    };

    const handleSideImageChangeClick = (modelSide: ModelSide) => {
        openFileDialog((files) => {
            if (files.length > 0) {
                showVariantsImageOptions(modelSide, (target) => changeImage(target, files[0], false, isDefaultImageScaled));
            }
        }, '.jpg,.png,.jpeg,.webp,.heic,.avif');
    };

    const handleSideAiGenerateClick = (modelSide: ModelSide) => {
        const existingSides = buildExistingImageRefs();
        showDialog(
            'side-ai-generate',
            <SideAiGenerateDialog
                existingSides={existingSides}
                defaultReferenceImageUrl={pickDefaultReferenceImage(modelSide)}
                generatePreview={async (opts) => {
                    const referenceUrl = opts.mode === 'existing' ? (opts.referenceImageUrl ?? undefined) : undefined;
                    return await generateSideImage({ prompt: opts.prompt, sideImageUrl: referenceUrl });
                }}
                allowExistingOption={existingSides.length > 0}
                onClose={() => closeDialog('side-ai-generate')}
                onNoCredits={() => {
                    closeDialog('side-ai-generate');
                    showSideImagesNoCreditsDialog();
                }}
                onApply={(url) => {
                    closeDialog('side-ai-generate');
                    // After the preview is confirmed, ask where to apply (only here / all / all with an attribute).
                    showVariantsImageOptions(modelSide, (target) => setImageFromUrl(target, url, false));
                }}
            />
        );
    };

    // Mostra il selettore di scope (questa variante / tutte / tutte con un attributo) e applica `apply`
    // al modelSide bersaglio, indipendentemente dall'origine dell'immagine (upload o AI).
    const showVariantsImageOptions = (modelSide: ModelSide, apply: (target: ModelSide) => void) => {
        const code = findModelColorForModelSide(state.product, modelSide)?.code ?? '';
        const originalSide = state.sides.find((x) => x.guid === modelSide.originalSideGuid)!;
        showDialog(
            'apply-image-variant',
            <VariantDialog
                code={code}
                side={originalSide}
                type={'PRINT_AREA'}
                firstCallback={() => apply(modelSide)}
                allCallback={() => handleApplyImageToAll(modelSide, apply)}
                variantCallback={(color) => handleApplyImageToAllVariant(modelSide, color, apply)}
            />
        );
    };

    const handleApplyImageToAll = (modelSide: ModelSide, apply: (target: ModelSide) => void) => {
        if (modelSide) {
            state.product.colors.forEach((modelColor) => {
                const thisColorModelSide = modelColor.sides.find((x) => x.originalSideGuid == modelSide.originalSideGuid);

                if (thisColorModelSide) {
                    apply(thisColorModelSide);
                }
            });
        }
    };

    const handleApplyImageToAllVariant = (modelSide: ModelSide, code: string, apply: (target: ModelSide) => void) => {
        if (modelSide) {
            state.product.colors
                .filter((x) => {
                    const thisColorModel = JSON.parse(x.code) as Array<Color>;
                    let found = false;
                    for (let attribute of thisColorModel) {
                        if (attribute.Value.Id === code) {
                            found = true;
                        }
                    }
                    return found;
                })
                .forEach((modelColor) => {
                    const thisColorModelSide = modelColor.sides.find((x) => x.originalSideGuid == modelSide.originalSideGuid);
                    if (thisColorModelSide) {
                        apply(thisColorModelSide);
                    }
                });
        }
    };

    // Picks the reference image for image-to-image bulk generation of a side. In order:
    //   1) the same side type from any variant that already has an image;
    //   2) otherwise the variant's own Side 1;
    //   3) otherwise Side 1 of any variant; else the default placeholder.
    const imageForSideType = (sideGuid?: string): string | null => {
        if (!sideGuid) return null;
        for (const color of state.product.colors) {
            const ms = color.sides.find((s) => s.originalSideGuid === sideGuid);
            if (ms && ms.imageUrl && ms.imageUrl !== defaultModelSideImageUrl) return ms.imageUrl;
        }
        return null;
    };

    const resolveBulkReferenceUrl = (modelColor: ModelColor, modelSide: ModelSide): string => {
        const sameSideType = imageForSideType(modelSide.originalSideGuid);
        if (sameSideType) return sameSideType;

        const firstSideGuid = state.sides[0]?.guid;
        const variantSide1 = modelColor.sides.find((s) => s.originalSideGuid === firstSideGuid);
        if (variantSide1 && variantSide1.imageUrl && variantSide1.imageUrl !== defaultModelSideImageUrl) return variantSide1.imageUrl;

        return imageForSideType(firstSideGuid) ?? defaultModelSideImageUrl;
    };

    // Runs the AI print-area predictor on a hosted image URL and returns a ready-to-apply area positioned on
    // the highest-confidence detection (origin at the image center). When the predictor returns no detection,
    // falls back to a centered default area so every generated side gets at least one area. Null only on failure.
    const predictAreaForImage = (imageUrl: string): Promise<{ area: ModelArea; ppcm: number | null } | null> =>
        new Promise((resolve) => {
            commitPredictPrintAreas({
                variables: { input: { imageUrl, includeAnnotatedImage: false } },
                onCompleted: (response: any) => {
                    const result = response?.predictPrintAreas;
                    const detections = result?.detections ?? [];
                    if (!result?.success) {
                        resolve(null);
                        return;
                    }

                    const ppcm = result.imageInfo?.ppcmAvg ?? null;
                    if (detections.length > 0) {
                        const best = detections.reduce((a: any, b: any) => (b.confidence > a.confidence ? b : a));
                        const box = best.bboxPixels;
                        const imgW = result.imageInfo?.width || box.width;
                        const imgH = result.imageInfo?.height || box.height;
                        const area = createAreaFromBoundsPX(box.x - imgW / 2, box.y - imgH / 2, box.width, box.height);
                        resolve({ area, ppcm });
                        return;
                    }

                    // No detection: fall back to a centered default area sized from the image dimensions.
                    const imageWidth = result.imageInfo?.width;
                    const imageHeight = result.imageInfo?.height;
                    if (imageWidth && imageHeight) {
                        resolve({ area: createDefaultCenteredArea(imageWidth, imageHeight), ppcm });
                        return;
                    }
                    resolve(null);
                },
                onError: () => resolve(null)
            });
        });

    // Bulk AI generation for the product's variants: for each (variant, side) in scope, generate the image
    // (image-to-image from a reference when "existing", text-to-image when "scratch") and/or predict the
    // print area on the generated image. Runs sequentially with a progress dialog.
    const handleBulkGenerateClick = () => {
        // Single attribute values across variants (not full combinations): "Green-L" / "Green / L" -> Green, L.
        const splitAttributes = (name: string) =>
            name
                .split(/\s*[-/]\s*/)
                .map((t) => t.trim())
                .filter(Boolean);

        const attributeValues = Array.from(new Set(state.product.colors.filter((c) => c.colorID !== 1).flatMap((c) => splitAttributes(c.name))));
        const sides = state.sides.map((s) => ({ guid: s.guid, name: s.name }));

        showDialog(
            'bulk-ai-variant-images',
            <BulkAiImageDialog
                attributeValues={attributeValues}
                sides={sides}
                onClose={() => closeDialog('bulk-ai-variant-images')}
                onGenerate={async (opts) => {
                    closeDialog('bulk-ai-variant-images');

                    const baseColor = state.product.colors.find((c) => c.colorID === 1);
                    const targetColors = state.product.colors.filter((modelColor) => {
                        if (modelColor.colorID === 1) return false;
                        if (!opts.attributeFilter) return true;
                        return splitAttributes(modelColor.name).includes(opts.attributeFilter);
                    });

                    // (variant, side) cells in scope.
                    const cells: { modelColor: ModelColor; modelSide: ModelSide }[] = [];
                    targetColors.forEach((modelColor) => {
                        modelColor.sides
                            .filter((modelSide) => !opts.sideFilter || modelSide.originalSideGuid === opts.sideFilter)
                            .forEach((modelSide) => cells.push({ modelColor, modelSide }));
                    });
                    if (cells.length === 0) return;

                    const showBulkProgress = (done: number) =>
                        showDialog(
                            'bulk-ai-progress',
                            <Dialog>
                                <UploadProgressRow
                                    label={stringFormat(T._('Generating variants with AI... ({0}/{1})', 'Admin'), String(done), String(cells.length))}
                                    status='PROCESSING'
                                    progress={Math.round((done / cells.length) * 100)}
                                />
                            </Dialog>
                        );

                    const runBulk = async () => {
                        showBulkProgress(0);
                        try {
                            let done = 0;
                            for (const { modelColor, modelSide } of cells) {
                                let generatedUrl: string | undefined;

                                if (opts.generateImage) {
                                    // 'existing' → image-to-image from an already-configured reference; 'scratch' → text-to-image.
                                    const referenceUrl = opts.source === 'existing' ? resolveBulkReferenceUrl(modelColor, modelSide) : undefined;
                                    generatedUrl = await generateSideImage({ prompt: opts.prompt, sideImageUrl: referenceUrl });
                                    setImageFromUrl(modelSide, generatedUrl, false);
                                }

                                if (opts.generatePrintArea) {
                                    // Predict on the just-generated image (its hosted URL) or the side's current image.
                                    const predictUrl =
                                        generatedUrl ??
                                        (modelSide.imageUrl && modelSide.imageUrl !== defaultModelSideImageUrl ? modelSide.imageUrl : undefined);
                                    if (predictUrl) {
                                        const predicted = await predictAreaForImage(predictUrl);
                                        if (predicted) {
                                            // Prefer the PPCM estimated by the predictor; fall back to the side's or base color's.
                                            const baseSide = baseColor?.sides.find((s) => s.originalSideGuid === modelSide.originalSideGuid);
                                            const ppcm = predicted.ppcm && predicted.ppcm > 0 ? predicted.ppcm : modelSide.ppcm || baseSide?.ppcm || 0;
                                            dispatch(mergeModelSideAction(modelSide, { areas: [predicted.area], ppcm }));
                                        }
                                    }
                                }

                                done++;
                                showBulkProgress(done);
                            }
                            closeDialog('bulk-ai-progress');
                            showNotification({
                                type: 'success',
                                message: <>{T._('Variants generated with AI. Remember to save to apply the changes.', 'Admin')}</>,
                                dismiss: { duration: 4000 }
                            });
                        } catch (ex) {
                            closeDialog('bulk-ai-progress');
                            if (ex instanceof AIStudioNoCreditsError) {
                                // Credits exhausted mid-run: invite the seller to top up (already-generated variants stay applied).
                                showSideImagesNoCreditsDialog();
                            } else {
                                showNotification({
                                    type: 'danger',
                                    message: T._('Some variants could not be generated. Please try again.', 'Admin'),
                                    dismiss: { duration: 4000 }
                                });
                            }
                        }
                    };

                    // Only image generation consumes AI Studio credits: gate it on availability (activates the addon
                    // when inactive, opens the purchase dialog on NO_CREDITS) before queuing any task. Predict-only runs skip it.
                    if (opts.generateImage) {
                        // Side generation must not enable credit auto-refill on first addon activation.
                        await runWithPreflight(runBulk, { autoRefill: false, onNoCredits: showSideImagesNoCreditsDialog });
                    } else {
                        await runBulk();
                    }
                }}
            />
        );
    };

    const handleSideAreaChangeClick = (modelSide: ModelSide, modelColor: ModelColor) => {
        // Manually edit the url
        setCurrentEditingModelSide(modelSide);
        setCurrentEditingModelOptions(
            state.product.options.find((x) => x.colorGuid === modelColor.guid && (x.sideGuid === modelSide?.guid || x.sideGuid === null)) ?? null
        );

        navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/list/print-area-editor')), { state: location.state });
    };

    const handleApplyAreaToAll = (newModelSide: ModelSide, newModelOptions: ModelOptions | null) => {
        if (currentEditingModelSide) {
            state.product.colors.forEach((modelColor) => {
                const thisColorModelSide = modelColor.sides.find((x) => x.originalSideGuid == currentEditingModelSide.originalSideGuid);

                if (thisColorModelSide) {
                    dispatch(
                        mergeModelSideAction(thisColorModelSide, {
                            areas: newModelSide.areas.map((x) => ({
                                ...x,
                                layers: x.layers.map((y) => ({
                                    ...y,
                                    id: toGlobalID(uuid(), 'AreaLayer')
                                }))
                            })),
                            ppcm: newModelSide.ppcm,
                            canRotate: newModelSide.canRotate
                        })
                    );
                }

                if (newModelOptions) {
                    dispatch(upsertModelOptionsAction({ ...newModelOptions, colorGuid: modelColor.guid, optionID: null }));
                } else if (currentEditingModelOptions) {
                    dispatch(removeModelOptionsAction(modelColor));
                }
            });
        }
    };

    const handleApplyAreaToAllVariant = (newModelSide: ModelSide, newModelOptions: ModelOptions | null, code: string) => {
        if (currentEditingModelSide) {
            state.product.colors
                .filter((x) => {
                    const thisColorModel = JSON.parse(x.code) as Array<Color>;
                    let found = false;
                    for (let attribute of thisColorModel) {
                        if (attribute.Value.Id === code) {
                            found = true;
                        }
                    }
                    return found;
                })
                .forEach((modelColor) => {
                    const thisColorModelSide = modelColor.sides.find((x) => x.originalSideGuid == currentEditingModelSide.originalSideGuid);

                    if (thisColorModelSide) {
                        dispatch(
                            mergeModelSideAction(thisColorModelSide, {
                                areas: newModelSide.areas.map((x) => ({
                                    ...x,
                                    layers: x.layers.map((y) => ({
                                        ...y,
                                        id: toGlobalID(uuid(), 'AreaLayer')
                                    }))
                                })),
                                ppcm: newModelSide.ppcm,
                                canRotate: newModelSide.canRotate
                            })
                        );
                    }

                    if (newModelOptions) {
                        dispatch(upsertModelOptionsAction({ ...newModelOptions, colorGuid: modelColor.guid, optionID: null }));
                    } else if (currentEditingModelOptions) {
                        dispatch(removeModelOptionsAction(modelColor));
                    }
                });
        }
    };

    const showMultipleVariantsOption = (
        code: string,
        side: ModelSide,
        type: 'PRINT_AREA' | 'IMAGE',
        firstCallback: () => void,
        allCallback: () => void,
        variantCallback: (color: string) => void
    ) => {
        const originalSide = state.sides.find((x) => x.guid === side.originalSideGuid)!;
        showDialog(
            'apply-image-variant',
            <VariantDialog
                code={code}
                side={originalSide}
                type={type}
                firstCallback={() => firstCallback()}
                allCallback={() => allCallback()}
                variantCallback={(color) => variantCallback(color)}
            />
        );
    };

    const handleSideToggleClick = (colorGuid: string, sideGuid: string) => {
        let newDisabledSides = state.disabledModelSides;

        const existent = newDisabledSides.find((x) => x.colorGuid == colorGuid && x.sideGuid == sideGuid);

        // Check if the model side does not exist
        const modelColor = state.product.colors.find((x) => x.guid == colorGuid);
        let modelSide: ModelSide | null = null;

        if (modelColor) {
            modelSide = modelColor.sides.find((x) => x.originalSideGuid == sideGuid) || null;
        }

        // Check if the variant is not resizable, multi canvas or with border
        const isResizableOrMultiCanvasOrBorder = state.product.options.some(
            (x) => x.colorGuid === colorGuid && (x.isResizable || x.isSplitOutput || x.mirrorType !== 'NONE')
        );

        if (isResizableOrMultiCanvasOrBorder) {
            showDialog(
                'all-resizable-variants',
                <MessageDialog message={T._('You cannot add more sides because the variant is resizable, with border, or is multi-canvas.', 'Admin')} />
            );
            return;
        }

        if (existent || !modelSide) {
            // Enabled the side (and create the modelSide if does not exist)
            if (existent) {
                newDisabledSides = update(newDisabledSides, {
                    $splice: [[newDisabledSides.indexOf(existent), 1]]
                });
            }

            if (modelColor && !modelSide) {
                const side = state.sides.find((x) => x.guid == sideGuid);
                if (side) dispatch(addEmptyModelSideAction(modelColor, side));
            }
        } else {
            // Disable the side
            newDisabledSides = update(newDisabledSides, {
                $push: [{ colorGuid, sideGuid }]
            });
        }

        dispatch(setDisabledSidesAction(newDisabledSides));
    };

    const handleSetMandatorySide = (value: boolean, modelSide: ModelSide) => {
        const originalSide = state.sides.find((x) => x.guid === modelSide.originalSideGuid)!;
        showDialog(
            'apply-mandatory',
            <SideDialog
                side={originalSide}
                firstCallback={() => dispatch(mergeModelSideAction(modelSide, { ...modelSide, mandatory: value }))}
                allCallback={() => dispatch(editSideMandatoryAllSides(modelSide, value))}
                variantCallback={() => dispatch(editSideMandatoryAllVariants(modelSide, value))}
                dialogKey={'apply-mandatory'}
            />
        );
    };

    const handleSideChangeRotate = (value: boolean, modelSide: ModelSide) => {
        const originalSide = state.sides.find((x) => x.guid === modelSide.originalSideGuid)!;

        showDialog(
            'apply-rotate',
            <SideDialog
                side={originalSide}
                firstCallback={() => dispatch(mergeModelSideAction(modelSide, { ...modelSide, canRotate: value }))}
                allCallback={() => dispatch(editSideRotateAllSides(modelSide, value))}
                variantCallback={() => dispatch(editSideRotateAllVariants(modelSide, value))}
                dialogKey={'apply-rotate'}
            />
        );
    };

    const handleVariantAddClick = (showMapping?: boolean) => {
        if (state.ecommerceSalesChannel.some((e) => e === 'NONE') && !showMapping) {
            navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/scratch')));
            return;
        }

        showMapping === true ? setShowMappingDialog(true) : navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/add')));
    };

    const handlePreviewClick = () => {
        // First ensure that we will not remain with a color without any side
        const emptyColors = state.product.colors.filter((modelColor) => {
            const remainSidesLength = modelColor.sides
                .map((x) => x.originalSideGuid)
                .filter((x) => state.disabledModelSides.find((j) => j.colorGuid == modelColor.guid && j.sideGuid == x) === undefined).length;

            return remainSidesLength == 0;
        });

        if (emptyColors.length > 0) {
            showDialog(
                'empty-color',
                <MessageDialog message={stringFormat(T._(`Variant '{0}' should have at last one side enabled.`, 'Admin'), emptyColors[0].name)} />
            );
            return;
        }

        state.disabledModelSides.forEach((disabledSide) => {
            const modelColor = state.product.colors.find((x) => x.guid == disabledSide.colorGuid);
            if (modelColor) {
                const modelSide = modelColor.sides.find((side) => side.originalSideGuid == disabledSide.sideGuid);
                if (modelSide) dispatch(removeModelSideAction(modelSide));
            }
        });

        changeStep('preview');
    };

    const onSaveVariants = () => {
        setModelOptions(() => {
            let modelColorsCodes = state.product.colors.map((c) => JSON.parse(c.code || '[]') as ColorAttribute[]);
            let modelOptions: merchantProductOptionType[] = [];
            if (state.product.colors.length > 0) {
                for (let i = 0; i < modelColorsCodes[0].length; i++) {
                    modelOptions.push({
                        isGlobal: false,
                        optionID: modelColorsCodes[0][i].Id,
                        label: modelColorsCodes[0][i].Label,
                        values: []
                    });
                    modelColorsCodes.forEach((m) => {
                        modelOptions[i].values.push({
                            valueID: m[i].Value.Id,
                            label: m[i].Value.Label
                        });
                    });
                    modelOptions[i].values = [...new Set(modelOptions[i].values.map((o) => JSON.stringify(o)))].map((o2) => JSON.parse(o2));
                }
            }
            return modelOptions;
        });

        navigate(getUrlWithQueryParams(backofficeUrl('/product/variations')));
    };

    const handleClickJsonSettings = (modelColor: ModelColor) => {
        showDialog(
            'json-settings',
            <JSONSettingsDialog
                modelColor={modelColor}
                sides={state.sides}
                onClose={() => closeDialog('json-settings')}
                onSave={(newModelColor: { settings: { colorProviderCode: string | null; sideCodes: { [sideGuid: string]: string } } }) => {
                    dispatch(editColorJsonSettingsAction(modelColor, newModelColor.settings));
                    closeDialog('json-settings');
                }}
            />
        );
    };

    useEffect(() => {
        if (showMappingDialog) {
            let supportedAdditionalCodesForVariants = state.product.additionalCodes.filter((ac) => {
                return !unsupportedSalesChannelsVariantsIDs.some((x) => toLocalID(x) === toLocalID(ac.salesChannelId));
            });

            showDialog(
                'mapping-variants',
                <ConnectModelDialog
                    windowDecorator={CustomDialogWindow}
                    forAddingNewSalesChannel={false}
                    forEditProduct={true}
                    buttonSaveText={T._('Apply', 'Admin')}
                    productID={state.product.id ?? ''}
                    additionalCodes={supportedAdditionalCodesForVariants.map((c) => {
                        return { code: c.code, salesChannelID: c.salesChannelId, merchantProductName: c.merchantProductName };
                    })}
                    productColors={state.product.colors}
                    salesChannels={query.salesChannels as Mutable<PartialSalesChannel>[]}
                    selectedSalesChannelID={state.salesChannelID}
                    productSalesChannelID={state.salesChannelID}
                    modelColorMappings={state.product.colorMappings}
                    merchantProductOptionsConnected={merchantProductOptionsConnected as MerchantProductOption[]}
                    mainStoreVariantsRemoved={storeVariantsRemoved}
                    onCloseDialog={() => setShowMappingDialog(false)}
                    onConfirm={(product, salesChannelID, productColorMappingsToSave) => {
                        closeDialog('mapping-variants');
                        setShowMappingDialog(false);
                        dispatch(editModelColorMappingsSalesChannelsAction(productColorMappingsToSave));
                        navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/list/')));
                    }}
                />
            );
        } else closeDialog('mapping-variants');
    }, [showMappingDialog]);

    // Render
    return (
        <React.Fragment>
            {routeParams.page === 'variations' && !routeParams.subpage && (
                <>
                    {/* Variants not supported */}
                    {!areVariantsSupported && <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/variations/variants-not-supported'))} replace />}

                    {/* Variants not defined in the store */}
                    {areVariantsSupported && !hasVariants && (
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/variations/no-store-variants'))} replace />
                    )}

                    {/* Variants defined correctly and at least one variant has been imported. */}
                    {areVariantsSupported && hasVariants && colors.length > 0 && (
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/variations/list/'))} replace />
                    )}

                    {/* Variants defined correctly and no variant has been imported yet. */}
                    {areVariantsSupported && hasVariants && colors.length === 0 && (
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/variations/add/'))} replace />
                    )}

                    {/* Menu soruce variants for scratch products */}
                    {state.ecommerceSalesChannel.some((e) => e === 'NONE') && !hasVariants && (
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/variations/menu-source-variants'))} replace />
                    )}

                    {/* Resizable product */}
                    {getProductIsResizable(state.product) && query.seller?.marketplace !== 'HP' && (
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/print-area'))} replace />
                    )}
                </>
            )}

            {routeParams.page === 'variations' &&
                routeParams.subpage === 'list' &&
                (colors.length > 0 || (state.ecommerceSalesChannel.some((e) => e === 'NONE') && state.ecommerceSalesChannel.length == 1)) && (
                    <>
                        <VariantsList
                            isSidesPerVariantEnabled={subscriptionResponse.subscription?.hasSidesPerVariant || false}
                            onSideToggleClick={handleSideToggleClick}
                            onSideAreaChangeClick={handleSideAreaChangeClick}
                            onSideImageChangeClick={handleSideImageChangeClick}
                            onSideAiGenerateClick={handleSideAiGenerateClick}
                            onBulkGenerateClick={handleBulkGenerateClick}
                            onVariantAddClick={handleVariantAddClick}
                            onVariantRemoveClick={handleVariantRemoveClick}
                            onVariantEditClick={handleVariantEditClick}
                            onPreviewClick={handlePreviewClick}
                            onBackClick={() =>
                                isFromScratch ? navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/scratch'))) : changeStep('print-area')
                            }
                            onSetMandatorySide={handleSetMandatorySide}
                            onSetRotateSide={handleSideChangeRotate}
                            onClickJsonSettings={handleClickJsonSettings}
                            unsupportedSalesChannelsVariantsIDs={unsupportedSalesChannelsVariantsIDs}
                            hasMappingMismatch={hasMappingMismatch}
                            mismatchedVariantGuids={mismatchedVariantGuids.current}
                            fixedVariantGuids={fixedVariantGuids}
                            storeVariantsRemoved={storeVariantsRemoved}
                        />

                        {currentEditingModelSide &&
                            routeParams.subpage2 === 'print-area-editor' &&
                            createPortal(
                                <PrintAreaEditorDialog
                                    modelColor={findModelColorForModelSide(state.product, currentEditingModelSide)!}
                                    modelSide={currentEditingModelSide}
                                    modelOptions={currentEditingModelOptions}
                                    disabledModelSides={state.disabledModelSides}
                                    onClose={() => {
                                        window.history.back();
                                    }}
                                    onSave={(newModelSide, newModelOptions) => {
                                        navigate(-1);
                                        const currentColor = findModelColorForModelSide(state.product, currentEditingModelSide)!;

                                        if (subscriptionResponse.subscription?.hasSidesPerVariant && state.product.colors.length > 1) {
                                            showMultipleVariantsOption(
                                                currentColor.code,
                                                currentEditingModelSide,
                                                'PRINT_AREA',
                                                () => {
                                                    dispatch(mergeModelSideAction(currentEditingModelSide, newModelSide));

                                                    // Model options
                                                    if (newModelOptions) {
                                                        dispatch(upsertModelOptionsAction(newModelOptions));
                                                    } else if (currentEditingModelOptions) {
                                                        dispatch(removeModelOptionsAction(currentColor));
                                                    }
                                                },
                                                () => handleApplyAreaToAll(newModelSide, newModelOptions),
                                                (code) => handleApplyAreaToAllVariant(newModelSide, newModelOptions, code)
                                            );
                                        } else {
                                            handleApplyAreaToAll(newModelSide, newModelOptions);
                                        }
                                    }}
                                />,
                                dialogsPortal
                            )}
                    </>
                )}

            {routeParams.page === 'variations' &&
                routeParams.subpage === 'list' &&
                colors.length === 0 &&
                state.ecommerceSalesChannel.some((e) => e === 'NONE') &&
                state.ecommerceSalesChannel.length == 1 && (
                    <>
                        <AddVariant
                            options={query.merchantProductOptions}
                            onAddVariantClick={handleAddVariationClick}
                            onCancelClick={() => changeStep('print-area')}
                            importImageVariantForSalesChannel={importImageVariantForSalesChannel}
                        />
                    </>
                )}

            {routeParams.page === 'variations' &&
                routeParams.subpage === 'list' &&
                colors.length === 0 &&
                !state.ecommerceSalesChannel.some((e) => e === 'NONE') &&
                state.ecommerceSalesChannel.length === 1 && (
                    <>
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/preview'))} replace />
                    </>
                )}

            {routeParams.page === 'variations' && routeParams.subpage === 'menu-source-variants' && (
                <>
                    <MenuSourceVariants
                        onChooseScratch={() => navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/scratch')))}
                        onChooseImport={() => navigate(getUrlWithQueryParams(backofficeUrl('/product/variations')))}
                        productId={state.product.id || ''}
                    ></MenuSourceVariants>

                    {/* <EditVariantsOffline onSaveVariants={onSaveVariants} /> */}
                </>
            )}

            {routeParams.page === 'variations' && routeParams.subpage === 'scratch' && (
                <>
                    <EditVariantsOffline onSaveVariants={onSaveVariants} />
                </>
            )}

            {routeParams.page === 'variations' && routeParams.subpage === 'add' && (
                <>
                    <AddVariant
                        options={query.merchantProductOptions}
                        onAddVariantClick={handleAddVariationClick}
                        onCancelClick={() => changeStep('print-area')}
                        importImageVariantForSalesChannel={importImageVariantForSalesChannel}
                    />
                </>
            )}

            {routeParams.page === 'variations' && routeParams.subpage === 'no-store-variants' && (
                <>
                    <ContainerBase>
                        <Section>
                            <NoVariantsMessage>
                                <strong>Oops!</strong>
                                <br />
                                {T._(`It seems you haven't set any variants for this product in your store.`, 'Admin')}
                                <br />
                                {T._(`Zakeke gets the product variants directly from your store, please set them in the store first.`, 'Admin')}
                                <br />
                                <br />
                                <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360015501299-Variants'>
                                    {T._('Learn more', 'Admin')}
                                </Link>
                            </NoVariantsMessage>
                        </Section>
                    </ContainerBase>
                    <PageFooterButtons $fixed={true} fullWidth leftContainerContent={<WizardFooterBackButton onClick={() => changeStep('print-area')} />}>
                        <Button onClick={handlePreviewClick}>{T._(`Go to preview`, 'Admin')}</Button>
                    </PageFooterButtons>
                </>
            )}

            {routeParams.page === 'variations' && routeParams.subpage === 'variants-not-supported' && (
                <>
                    <ContainerBase>
                        <Section>
                            <NoVariantsMessage>
                                <strong>Oops!</strong>
                                <br />
                                {T._(`Variants are not supported yet.`, 'Admin')}
                                <br />
                                {T._(`Please write us at`, 'Admin') + ' '}
                                <Link href={'mailto: help@zakeke.com'}>help@zakeke.com</Link>
                            </NoVariantsMessage>
                        </Section>
                    </ContainerBase>
                    <PageFooterButtons $fixed={true} fullWidth leftContainerContent={<WizardFooterBackButton onClick={() => changeStep('print-area')} />}>
                        <Button onClick={handlePreviewClick}>{T._(`Go to preview`, 'Admin')}</Button>
                    </PageFooterButtons>
                </>
            )}
        </React.Fragment>
    );
};

const VariantsError = () => {
    const changeStep = useProductConfigurationChangeStep();

    return (
        <>
            <ContainerBase>
                <Section>
                    <ErrorIcon>
                        <PageErrorIcon />
                    </ErrorIcon>
                    <NoVariantsMessage>
                        <strong>Oops!</strong>
                        <br />
                        {T._('It seems that this product has been removed from your store or is no longer accessible.', 'Admin')}
                        <br />
                        {T._('Please check the product in your store and try again.', 'Admin')}
                    </NoVariantsMessage>
                </Section>
            </ContainerBase>
            <PageFooterButtons $fixed={true} fullWidth leftContainerContent={<WizardFooterBackButton onClick={() => changeStep('print-area')} />}>
                <Button onClick={() => changeStep('preview')}>{T._('Go to preview', 'Admin')}</Button>
            </PageFooterButtons>
        </>
    );
};

const Variants = () => {
    return (
        <CustomErrorBoundary render={() => <VariantsError />}>
            <VariantsPage />
        </CustomErrorBoundary>
    );
};

export default Variants;
